import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { ListGroup, Button, Spinner } from 'react-bootstrap';
import {
  FaEdit,
  FaTrash,
  FaBan,
  FaChevronLeft,
  FaChevronRight,
  FaPoll,
} from 'react-icons/fa';
import './PollsList.css';
import ContentUnitTag from '../common/ContentUnitTag';
import { db, auth } from '../../firebase/firebase-config';
import { useUser } from '../../context/UserContext';
import { useTenant } from '../../context/TenantContext';
import {
  POLLS_PAGE_SIZE,
  buildPollsPageQuery,
  buildPinnedPollsQuery,
  fetchPollsPage,
  mapPollDoc,
  markPollAsRead,
  loadUserVotedPollIds,
} from '../../services/pollsService';
import { filterByActiveTenant } from '../../services/tenantContentFilter';
import { canViewAllUnitsForTenant } from '../../services/tenantListQuery';
import { getVisibleUnitIds } from '../../services/contentQueryHelpers';
import { useMembership } from '../../context/MembershipContext';
import { usePaginatedTenantList } from '../../hooks/usePaginatedTenantList';
import { LAST_READ_MODULES } from '../../platform/lastRead/lastReadModules';
import { useUnreadItemIds } from '../../platform/lastRead/useUnreadItemIds';
import { isPinnedActive, sortPinnedItems } from '../../platform/pinnedContent';
import PinnedHeroCard, { PinnedHeroSection } from '../../platform/PinnedHeroCard';
import ContentListFeedSection from '../../platform/ContentListFeedSection';
import { useConfirm } from '../../platform/confirm/ConfirmProvider';
import { useToast } from '../../platform/toast/ToastProvider';
import { plainTextExcerpt } from '../../platform/contentExcerpt';
import {
  PREMIUM_MODULE_IDS,
  premiumListClassName,
  showListNavigateHint,
} from '../../platform/contentListVisual';
import ListItemNavigateHint from '../../platform/ListItemNavigateHint';

async function fetchPollsPageItems(user, tenantId, memberships, options) {
  const { polls, lastVisible } = await fetchPollsPage(user, tenantId, memberships, options);
  return { items: polls, lastVisible };
}

function PollsListTab({ active, children, onClick }) {
  return (
    <button
      type="button"
      className={`polls-list-tab${active ? ' is-active' : ''}`}
      onClick={onClick}
      aria-pressed={active}
    >
      {children}
    </button>
  );
}

function PollsList({ onSelectPoll, onEditPoll, isAdmin, isLocalAdmin, onDeletePoll }) {
  const [votedPolls, setVotedPolls] = useState({});
  const [pinnedPolls, setPinnedPolls] = useState([]);
  const [voteFilter, setVoteFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const listTopRef = useRef(null);

  const { user, loading } = useUser();
  const { tenantId } = useTenant();
  const { memberships } = useMembership();

  const {
    items: polls,
    pageIndex,
    hasNextPage,
    pageLoading,
    goToPrevPage,
    goToNextPage,
    pageSize,
  } = usePaginatedTenantList({
    tenantId,
    user,
    memberships,
    userLoading: loading,
    pageSize: POLLS_PAGE_SIZE,
    buildPageQuery: buildPollsPageQuery,
    fetchPage: fetchPollsPageItems,
    mapDoc: mapPollDoc,
    logLabel: 'polls',
  });

  useEffect(() => {
    if (loading || !user || !tenantId) {
      setPinnedPolls([]);
      return undefined;
    }

    const pinnedQuery = buildPinnedPollsQuery(tenantId);
    const unsubscribe = onSnapshot(pinnedQuery, (querySnapshot) => {
      const items = filterByActiveTenant(
        querySnapshot.docs.map(mapPollDoc),
        tenantId,
      )
        .filter((poll) => isPinnedActive(poll))
        .filter((poll) => {
          if (canViewAllUnitsForTenant(user, memberships, tenantId)) {
            return true;
          }
          return getVisibleUnitIds(user).includes(poll.unit);
        });

      setPinnedPolls(sortPinnedItems(items));
    }, (error) => {
      console.error('Error fetching pinned polls:', error);
      setPinnedPolls([]);
    });

    return () => unsubscribe();
  }, [loading, user, tenantId, memberships]);

  const pinnedHeroIds = useMemo(
    () => new Set(pinnedPolls.map((item) => item.id)),
    [pinnedPolls],
  );

  const regularPolls = useMemo(
    () => polls.filter((poll) => !pinnedHeroIds.has(poll.id)),
    [polls, pinnedHeroIds],
  );

  const isContentAdmin = isAdmin || isLocalAdmin;

  const matchesPollFilter = useCallback((poll) => {
    if (isContentAdmin) {
      if (statusFilter === 'active' && !poll.active) return false;
      if (statusFilter === 'ended' && poll.active) return false;
      return true;
    }

    if (voteFilter === 'unvoted' && votedPolls[poll.id]) return false;
    if (voteFilter === 'voted' && !votedPolls[poll.id]) return false;
    return true;
  }, [isContentAdmin, statusFilter, voteFilter, votedPolls]);

  const filteredPinnedPolls = useMemo(
    () => pinnedPolls.filter(matchesPollFilter),
    [pinnedPolls, matchesPollFilter],
  );

  const filteredRegularPolls = useMemo(
    () => regularPolls.filter(matchesPollFilter),
    [regularPolls, matchesPollFilter],
  );

  const unreadPollIds = useUnreadItemIds(
    LAST_READ_MODULES.POLLS,
    [...pinnedPolls, ...regularPolls],
    { isEligible: (poll) => poll.active },
  );

  const scrollListIntoView = useCallback(() => {
    listTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const canManagePoll = (poll) => {
    if (isAdmin) return true;
    if (isLocalAdmin && poll.uid === user?.uid) return true;
    return false;
  };

  const reserveActionsColumn = Boolean(user?.uid);
  const confirm = useConfirm();
  const toast = useToast();

  useEffect(() => {
    const allPolls = [...pinnedPolls, ...regularPolls];
    if (loading || !user || allPolls.length === 0) {
      setVotedPolls({});
      return undefined;
    }

    let cancelled = false;

    const loadVotedPolls = async () => {
      try {
        const userId = auth.currentUser.uid;
        const nextVoted = await loadUserVotedPollIds(allPolls.map((poll) => poll.id), userId);

        if (!cancelled) {
          setVotedPolls(nextVoted);
        }
      } catch (error) {
        console.error('Error loading poll vote status:', error);
      }
    };

    loadVotedPolls();

    return () => {
      cancelled = true;
    };
  }, [pinnedPolls, regularPolls, loading, user]);

  const handleDelete = async (pollId) => {
    const confirmed = await confirm({
      title: 'Izbriši anketo',
      message: 'Ali ste prepričani, da želite izbrisati anketo?',
      confirmLabel: 'Izbriši',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await onDeletePoll(pollId);
      toast.deleted('Anketa je bila izbrisana.');
    } catch (error) {
      console.error('Error deleting poll:', pollId, error);
      toast.error('Ankete ni bilo mogoče izbrisati.');
    }
  };

  const handleEndPoll = async (pollId) => {
    const confirmed = await confirm({
      title: 'Končaj anketo',
      message: 'Ali ste prepričani, da želite končati anketo?',
      confirmLabel: 'Končaj',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await updateDoc(doc(db, 'polls', pollId), { active: false });
      toast.success('Anketa je bila končana.');
    } catch (error) {
      console.error('Error ending poll:', pollId, error);
      toast.error('Ankete ni bilo mogoče končati.');
    }
  };

  const handlePollClick = (poll) => {
    if (poll.active || isAdmin) {
      if (poll.active && auth.currentUser?.uid) {
        markPollAsRead(auth.currentUser.uid, poll.id).catch((error) => {
          console.error('Error marking poll as read:', poll.id, error);
        });
      }
      onSelectPoll(poll);
    }
  };

  const handlePrevPage = () => {
    if (goToPrevPage()) {
      scrollListIntoView();
    }
  };

  const handleNextPage = () => {
    if (goToNextPage()) {
      scrollListIntoView();
    }
  };

  const formatDate = (date) =>
    date?.toLocaleString('sl-SI', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }) ?? 'Ni datuma';

  const formatExpires = (date) =>
    date?.toLocaleString('sl-SI', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }) ?? 'Ne poteče';

  const isDefaultFilter = isContentAdmin ? statusFilter === 'all' : voteFilter === 'all';
  const showPinnedSection = pageIndex === 0 && isDefaultFilter && filteredPinnedPolls.length > 0;
  const displayedListPolls = useMemo(() => {
    if (isDefaultFilter) {
      return filteredRegularPolls;
    }
    return [...filteredPinnedPolls, ...filteredRegularPolls];
  }, [isDefaultFilter, filteredPinnedPolls, filteredRegularPolls]);
  const hasAnyItems = pinnedPolls.length > 0 || regularPolls.length > 0;
  const hasFilteredItems = displayedListPolls.length > 0 || (isDefaultFilter && filteredPinnedPolls.length > 0);
  const rangeStart = displayedListPolls.length ? pageIndex * pageSize + 1 : 0;
  const rangeEnd = pageIndex * pageSize + displayedListPolls.length;
  const showPagination = pageIndex > 0 || hasNextPage;
  const showNavigateHint = showListNavigateHint(PREMIUM_MODULE_IDS.POLLS);
  const listClassName = premiumListClassName(
    PREMIUM_MODULE_IDS.POLLS,
    'polls-list platform-content-list',
  );
  const wrapperClassName = [
    'content-list-wrapper',
    'platform-content-list-wrapper',
    showNavigateHint && 'platform-content-list-wrapper--card-stack',
  ].filter(Boolean).join(' ');

  const renderPollBadges = (poll) => (
    <>
      <span
        className={`poll-vote-badge ${
          poll.active ? 'poll-vote-badge--active' : 'poll-vote-badge--inactive'
        }`}
      >
        {poll.active ? 'Aktivna' : 'Končana'}
      </span>
      {poll.anonymous ? (
        <span className="poll-vote-badge poll-vote-badge--anonymous">
          Anonimna
        </span>
      ) : (
        <span className="poll-vote-badge poll-vote-badge--identified">
          Identificirana
        </span>
      )}
      <ContentUnitTag item={poll} />
    </>
  );

  const renderPinnedCard = (poll) => {
    const isUnread = unreadPollIds.includes(poll.id);
    const hasVoted = votedPolls[poll.id];
    const canManage = canManagePoll(poll);

    return (
      <PinnedHeroCard
        key={poll.id}
        pinnedUntil={poll.pinnedUntil}
        thumbnailUrl={poll.thumbnailUrl}
        placeholderIcon={FaPoll}
        title={poll.title}
        isUnread={isUnread}
        inactive={!poll.active}
        onClick={() => handlePollClick(poll)}
        badges={renderPollBadges(poll)}
        extra={(hasVoted || poll.active) && (
          <div className="polls-list-item-vote-status">
            {hasVoted ? (
              <span className="poll-vote-badge poll-vote-badge--voted">
                Glas oddan
              </span>
            ) : (
              <span className="poll-vote-badge poll-vote-badge--cta">
                Glasuj
              </span>
            )}
          </div>
        )}
        meta={(
          <>
            <span>{poll.displayName || 'Anonimen'}</span>
            <span className="benefit-list-item-separator">·</span>
            <span>{formatDate(poll.createdAt)}</span>
            <span className="benefit-list-item-separator">·</span>
            <span>Poteče {formatExpires(poll.expires)}</span>
          </>
        )}
        actions={canManage ? (
          <>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onEditPoll(poll);
              }}
            >
              <FaEdit /> Uredi
            </Button>
            {poll.active ? (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEndPoll(poll.id);
                }}
              >
                <FaBan /> Končaj
              </Button>
            ) : (
              <span className="pinned-hero-card__actions-spacer" aria-hidden="true" />
            )}
            <Button
              variant="outline-danger"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(poll.id);
              }}
            >
              <FaTrash /> Izbriši
            </Button>
          </>
        ) : undefined}
        actionsLayout="grid-3"
      />
    );
  };

  const pollExcerpt = (poll) => {
    const firstQuestion = poll.questions?.[0]?.question;
    if (firstQuestion) {
      return plainTextExcerpt(firstQuestion);
    }
    const count = poll.questions?.length ?? 0;
    return count > 0 ? `${count} vprašanj` : '';
  };

  const renderRegularItem = (poll) => {
    const isUnread = unreadPollIds.includes(poll.id);
    const hasVoted = votedPolls[poll.id];
    const canManage = canManagePoll(poll);
    const excerpt = pollExcerpt(poll);

    return (
      <ListGroup.Item
        key={poll.id}
        className={[
          'polls-list-item',
          'platform-content-list-item',
          isUnread ? 'polls-list-item--unread platform-content-list-item--unread' : '',
          !poll.active ? 'polls-list-item--inactive' : '',
        ].filter(Boolean).join(' ')}
        onClick={() => handlePollClick(poll)}
      >
        <div className={`polls-list-item-main platform-content-list-item-main${reserveActionsColumn ? ' polls-list-item-main--has-actions platform-content-list-item-main--has-actions' : ''}`}>
          <div className="polls-list-item-content platform-content-list-item-content">
            <div className="platform-content-list-item-thumb">
              {isUnread && (
                <span className="platform-content-list-item-unread-badge">
                  <span
                    className="platform-unread-dot"
                    title="Neprebrano"
                    aria-label="Neprebrano"
                  />
                </span>
              )}
              {poll.thumbnailUrl ? (
                <img src={poll.thumbnailUrl} alt="" loading="lazy" />
              ) : (
                <div className="platform-content-list-item-thumb-placeholder" aria-hidden="true">
                  <FaPoll />
                </div>
              )}
            </div>
            <div className="polls-list-item-body platform-content-list-item-body">
              <div className="polls-list-item-title platform-content-list-item-title">{poll.title}</div>
              <div className="polls-list-item-badges platform-content-list-item-badges">
                {renderPollBadges(poll)}
              </div>
              {excerpt && (
                <p className="platform-content-list-item-excerpt platform-content-list-item-excerpt--premium">
                  {excerpt}
                </p>
              )}
              {(hasVoted || poll.active) && (
                <div className="polls-list-item-vote-status">
                  {hasVoted ? (
                    <span className="poll-vote-badge poll-vote-badge--voted">
                      Glas oddan
                    </span>
                  ) : (
                    <span className="poll-vote-badge poll-vote-badge--cta">
                      Glasuj
                    </span>
                  )}
                </div>
              )}
              <div className="polls-list-item-footer platform-content-list-item-footer">
                <span>Ustvaril {poll.displayName || 'Anonimen'}</span>
                <span className="polls-list-item-separator">·</span>
                <span>{formatDate(poll.createdAt)}</span>
                <span className="polls-list-item-separator">·</span>
                <span>Poteče {formatExpires(poll.expires)}</span>
              </div>
            </div>
          </div>

          {reserveActionsColumn && (
            <div className="polls-list-item-aside platform-content-list-item-aside">
              <div className="platform-content-list-item-aside-inner">
                {canManage ? (
                  <div className="polls-list-item-actions platform-content-list-item-actions">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditPoll(poll);
                      }}
                    >
                      <FaEdit /> Uredi
                    </Button>
                    {poll.active && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEndPoll(poll.id);
                        }}
                      >
                        <FaBan /> Končaj
                      </Button>
                    )}
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(poll.id);
                      }}
                    >
                      <FaTrash /> Izbriši
                    </Button>
                  </div>
                ) : null}
                {showNavigateHint ? <ListItemNavigateHint /> : null}
              </div>
            </div>
          )}
        </div>
      </ListGroup.Item>
    );
  };

  if (loading) {
    return (
      <div className="platform-content-list-loading">
        <Spinner animation="border" role="status" size="sm">
          <span className="visually-hidden">Nalaganje...</span>
        </Spinner>
      </div>
    );
  }

  if (!pageLoading && !hasAnyItems) {
    return (
      <div className={`platform-content-list-empty${showNavigateHint ? ' platform-content-list-empty--premium' : ''}`}>
        <strong>Ni anket</strong>
        <span>Trenutno ni objavljenih anket za prikaz.</span>
      </div>
    );
  }

  return (
    <div className={wrapperClassName} ref={listTopRef}>
      <div className="polls-list-toolbar">
        <div className="polls-list-tabs" role="tablist" aria-label="Filtri anket">
          {isContentAdmin ? (
            <>
              <PollsListTab
                active={statusFilter === 'all'}
                onClick={() => setStatusFilter('all')}
              >
                Vse
              </PollsListTab>
              <PollsListTab
                active={statusFilter === 'active'}
                onClick={() => setStatusFilter('active')}
              >
                Aktivne
              </PollsListTab>
              <PollsListTab
                active={statusFilter === 'ended'}
                onClick={() => setStatusFilter('ended')}
              >
                Končane
              </PollsListTab>
            </>
          ) : (
            <>
              <PollsListTab
                active={voteFilter === 'all'}
                onClick={() => setVoteFilter('all')}
              >
                Vse ankete
              </PollsListTab>
              <PollsListTab
                active={voteFilter === 'unvoted'}
                onClick={() => setVoteFilter('unvoted')}
              >
                Brez glasu
              </PollsListTab>
              <PollsListTab
                active={voteFilter === 'voted'}
                onClick={() => setVoteFilter('voted')}
              >
                Glas oddan
              </PollsListTab>
            </>
          )}
        </div>
      </div>

      {pageLoading && (
        <div className="platform-content-list-loading platform-content-list-loading--overlay">
          <Spinner animation="border" role="status" size="sm">
            <span className="visually-hidden">Nalaganje...</span>
          </Spinner>
        </div>
      )}

      {!pageLoading && !hasFilteredItems && pageIndex === 0 ? (
        <div className={`platform-content-list-empty${showNavigateHint ? ' platform-content-list-empty--premium' : ''}`}>
          <strong>Ni anket</strong>
          <span>Ni anket za izbrani prikaz.</span>
        </div>
      ) : (
        <>
          {!pageLoading && showPinnedSection && (
            <PinnedHeroSection
              title="Pripete ankete"
              count={filteredPinnedPolls.length}
              ariaLabel="Pripete ankete"
            >
              {filteredPinnedPolls.map(renderPinnedCard)}
            </PinnedHeroSection>
          )}

          {!pageLoading && (displayedListPolls.length > 0 || pageIndex > 0) && (
            <ContentListFeedSection
              heading={showPinnedSection && displayedListPolls.length > 0 ? 'Ostale ankete' : null}
              ariaLabel="Seznam anket"
            >
              <ListGroup className={listClassName}>
                {displayedListPolls.map(renderRegularItem)}
              </ListGroup>
            </ContentListFeedSection>
          )}
        </>
      )}

      {showPagination && (
        <nav className="platform-content-list-pagination" aria-label="Strani anket">
          <Button
            variant="outline-secondary"
            size="sm"
            className="platform-content-list-pagination__btn"
            onClick={handlePrevPage}
            disabled={pageIndex === 0 || pageLoading}
          >
            <FaChevronLeft aria-hidden="true" />
            <span>Prejšnja</span>
          </Button>

          <div className="platform-content-list-pagination__info">
            <span className="platform-content-list-pagination__page">Stran {pageIndex + 1}</span>
            {displayedListPolls.length > 0 && (
              <span className="platform-content-list-pagination__range">
                Prikazano {rangeStart}–{rangeEnd}
              </span>
            )}
          </div>

          <Button
            variant="outline-secondary"
            size="sm"
            className="platform-content-list-pagination__btn"
            onClick={handleNextPage}
            disabled={!hasNextPage || pageLoading}
          >
            <span>Naslednja</span>
            <FaChevronRight aria-hidden="true" />
          </Button>
        </nav>
      )}
    </div>
  );
}

export default PollsList;
